import React, { useEffect, useMemo } from 'react'
import { useHistory, useParams, useRouteMatch } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Helmet } from 'react-helmet-async'

import { EditLocalization } from '../components/Localizations/EditLocalization'
import { PATH_PRODUCTS_EDIT } from '../consts'
import { Localization, useLocalizations } from '../hooks/useLocalizations'
import { useProduct } from '../hooks/useProduct'

export const EditLocalizationPage = () => {
  const { productId = null, localizationId = null } = useParams<{
    productId?: string
    localizationId?: string
  }>()
  const { product, error: productError } = useProduct(productId)
  const { localizations, refetchLocalizations } = useLocalizations(product?.id)
  const history = useHistory()
  const { url } = useRouteMatch()

  const currentLocalization = useMemo<Localization | undefined>(() => {
    if (!productId) return
    if (!localizationId && !localizations) return

    return localizations?.find((l) => l.id === localizationId) || localizations?.[0]
  }, [JSON.stringify(localizations), localizationId, productId]) // eslint-disable-line

  const pageTitle =
    `Edit Product #${productId}` +
    (currentLocalization
      ? ` | ${currentLocalization?.country}-${currentLocalization?.subdivision}`
      : '')

  useEffect(() => {
    if (productError?.response?.status === 404) {
      toast('Product ID does not exist.', { containerId: 'error' })
    }
  }, [productError])

  useEffect(() => {
    if (!localizations) return
    if (!productId) return

    const queryParams = history.location.search
    const found = localizations?.find((l) => l.id === localizationId)
    const defaultLocalization = localizations?.[0]

    if (!localizationId) {
      if (localizations.length === 0) return

      history.replace(
        `${PATH_PRODUCTS_EDIT}/${productId}/localizations/${defaultLocalization.id}${queryParams}`
      )
      return
    }

    if (localizations.length === 0) {
      history.replace(`${PATH_PRODUCTS_EDIT}/${productId}${queryParams}`)
      return
    }

    if (!found) {
      history.replace(
        `${PATH_PRODUCTS_EDIT}/${productId}/localizations/${defaultLocalization.id}${queryParams}`
      )
      return
    }

    return
  }, [localizationId, JSON.stringify(localizations), history, productId, url]) // eslint-disable-line

  // If there is no product do not render localization form
  if (!productId || !product) return null

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <EditLocalization
        product={product}
        localizations={localizations}
        currentLocalization={currentLocalization}
        refetchLocalizations={refetchLocalizations}
      />
    </>
  )
}
